/**
 * Product telemetry client — Pack 13.J.
 *
 * Owning task: Task #1106.
 *
 * Privacy-conscious in-house telemetry layer. Captures:
 *   - app.session_start / app.identified
 *   - app.page_view (on every route transition)
 *   - ui.primary_action (data-telemetry-action attribute on click)
 *   - feature.flag_evaluated (via trackFeatureFlagEval helper)
 *   - funnel events (via trackEvent("permit.create_started" …))
 *
 * Design constraints (DO NOT VIOLATE):
 *   1. Cookie consent gate — `hasCookieConsent("analytics")` MUST return
 *      true before any beacon is sent. The init() flow re-checks on
 *      `COOKIE_CONSENT_EVENT`.
 *   2. PII discipline — payloads pass through `scrubProperties` from
 *      `shared/telemetry-events.ts`. The library never sends user IDs,
 *      email addresses, names, or free-text descriptions.
 *   3. Per-tenant opt-out — at boot we GET /api/telemetry/opt-out; when
 *      `enabled === false` we go fully silent.
 *   4. Batched send — events accumulate in an in-memory queue and flush
 *      every 5s, on `pagehide`, or when the queue reaches 25 events.
 *
 * The client uses `navigator.sendBeacon` when available (so events fire
 * even on page-close) and falls back to `fetch(..., { keepalive: true })`.
 */
import {
  TELEMETRY_EVENT_KEYS,
  scrubProperties,
  normaliseSurface,
  roleBucketFromRole,
  type TelemetryRoleBucket,
} from "@shared/telemetry-events";
import {
  hasCookieConsent,
  COOKIE_CONSENT_EVENT,
} from "./cookie-consent";

const SESSION_STORAGE_KEY = "nashid:telemetry:session-id:v1";
const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE_BEFORE_FLUSH = 25;
const ENDPOINT = "/api/telemetry/events";

interface QueuedEvent {
  eventName: string;
  sessionId: string;
  occurredAt: string;
  surface?: string;
  properties: Record<string, unknown>;
}

interface TelemetryState {
  initialised: boolean;
  enabled: boolean;
  optOut: boolean;
  consentGranted: boolean;
  sessionId: string;
  tenantId: string | null;
  roleBucket: TelemetryRoleBucket | null;
  currentSurface: string | null;
  queue: QueuedEvent[];
  flushTimer: ReturnType<typeof setInterval> | null;
}

const state: TelemetryState = {
  initialised: false,
  enabled: false,
  optOut: false,
  consentGranted: false,
  sessionId: "",
  tenantId: null,
  roleBucket: null,
  currentSurface: null,
  queue: [],
  flushTimer: null,
};

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function generateSessionId(): string {
  // Prefer crypto.randomUUID when available (modern browsers + Node 20+).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback random.
  const rand = Math.random().toString(36).slice(2);
  return `s_${Date.now().toString(36)}_${rand}`;
}

function loadOrCreateSessionId(): string {
  if (!isBrowser()) return generateSessionId();
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
  } catch {
    // sessionStorage may throw in private browsing; fall through.
  }
  const fresh = generateSessionId();
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
  } catch {
    // Ignore — best-effort.
  }
  return fresh;
}

function shouldEmit(): boolean {
  return state.initialised && state.enabled && state.consentGranted && !state.optOut;
}

function refreshConsent(): void {
  state.consentGranted = isBrowser() ? hasCookieConsent("analytics") : false;
}

async function refreshOptOut(): Promise<void> {
  if (!isBrowser() || !state.tenantId) {
    state.optOut = false;
    return;
  }
  try {
    const r = await fetch("/api/telemetry/opt-out", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) {
      // Fail-open: assume not opted out.
      state.optOut = false;
      return;
    }
    const body = (await r.json()) as { enabled?: boolean };
    state.optOut = body.enabled === false;
  } catch {
    // Fail-open.
    state.optOut = false;
  }
}

function startFlushTimer(): void {
  if (!isBrowser() || state.flushTimer) return;
  state.flushTimer = setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
}

function stopFlushTimer(): void {
  if (state.flushTimer) {
    clearInterval(state.flushTimer);
    state.flushTimer = null;
  }
}

/**
 * Flush queued events to the ingest endpoint.
 * @internal exported for tests.
 */
export async function flush(): Promise<void> {
  if (state.queue.length === 0) return;
  const batch = state.queue.splice(0, state.queue.length);
  const payload = JSON.stringify({ events: batch });

  if (!isBrowser()) return;

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // sendBeacon is fire-and-forget but ideal for unload paths.
      const blob = new Blob([payload], { type: "application/json" });
      const queued = navigator.sendBeacon(ENDPOINT, blob);
      if (queued) return;
    }
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Drop on failure — telemetry must never break the user journey.
  }
}

/**
 * Enqueue an event. Drops silently when the gate isn't satisfied or the
 * event key is unknown. Properties are scrubbed before enqueue so that
 * even an in-memory leak (e.g. via devtools) never exposes PII.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: { surface?: string },
): void {
  if (!shouldEmit()) return;
  if (!TELEMETRY_EVENT_KEYS.has(eventName)) {
    if (typeof console !== "undefined" && import.meta.env?.DEV) {
      console.warn("[telemetry] unknown event dropped:", eventName);
    }
    return;
  }
  const surface = options?.surface ?? state.currentSurface ?? undefined;
  state.queue.push({
    eventName,
    sessionId: state.sessionId,
    occurredAt: new Date().toISOString(),
    surface: surface ? normaliseSurface(surface) : undefined,
    properties: scrubProperties(properties),
  });
  if (state.queue.length >= MAX_QUEUE_BEFORE_FLUSH) {
    void flush();
  }
}

/**
 * Identify the current actor's tenant + role bucket. We never accept
 * user IDs, names, or emails. Pass the literal role string from the
 * server session and we'll bucket it locally.
 */
export function identifyTenant(input: { tenantId: string | null; role: string | null }): void {
  state.tenantId = input.tenantId ?? null;
  state.roleBucket = roleBucketFromRole(input.role);
  void refreshOptOut().then(() => {
    if (state.tenantId) {
      trackEvent("app.identified", { roleBucket: state.roleBucket ?? "other" });
    }
  });
}

/** Track a route change. Surface is normalised (UUIDs → :id). */
export function trackPageView(rawPath: string): void {
  state.currentSurface = normaliseSurface(rawPath);
  trackEvent("app.page_view", { surface: state.currentSurface });
}

/** Track a feature flag evaluation (FEATURE_CATALOG or Unleash flag). */
export function trackFeatureFlagEval(flag: string, enabled: boolean, source: string): void {
  trackEvent("feature.flag_evaluated", { flag, enabled, source });
}

/**
 * Initialise the telemetry library. Idempotent — calling twice is a no-op.
 * Must be called from the top of the React tree on the client.
 */
export function initTelemetry(): void {
  if (state.initialised) return;
  state.initialised = true;
  state.enabled = true;
  state.sessionId = loadOrCreateSessionId();

  if (!isBrowser()) return;

  refreshConsent();
  startFlushTimer();

  // Listen for consent changes so users who toggle the banner mid-session
  // see immediate effect (both directions: grant → start emitting,
  // revoke → stop and drop pending queue).
  window.addEventListener(COOKIE_CONSENT_EVENT, () => {
    const wasGranted = state.consentGranted;
    refreshConsent();
    if (wasGranted && !state.consentGranted) {
      // Consent revoked — drop in-flight queue.
      state.queue.length = 0;
    }
  });

  // Flush on tab close / hide.
  window.addEventListener("pagehide", () => {
    void flush();
  });
  window.addEventListener("beforeunload", () => {
    void flush();
  });

  // Fire the lifecycle event once per session.
  trackEvent("app.session_start");
}

/**
 * Reset internal state. EXPORTED FOR TESTS ONLY — do not call from app code.
 * @internal
 */
export function __resetTelemetryForTests(): void {
  stopFlushTimer();
  state.initialised = false;
  state.enabled = false;
  state.optOut = false;
  state.consentGranted = false;
  state.sessionId = "";
  state.tenantId = null;
  state.roleBucket = null;
  state.currentSurface = null;
  state.queue.length = 0;
}

/** Internal accessor for tests. @internal */
export function __getTelemetryQueueForTests(): QueuedEvent[] {
  return state.queue.slice();
}

/** Internal accessor for tests. @internal */
export function __setTelemetryStateForTests(patch: Partial<TelemetryState>): void {
  Object.assign(state, patch);
}
